import type { AgentConfig } from '@kanbu/schema';
import {
  Agent,
  AgentType,
  DEFAULT_GEMINI_LIVE_MODEL,
  GeminiLiveModel,
  SourcesDisplayMode,
  SourcesFormat,
  geminiLiveModelSupportsThinking,
} from '@kanbu/schema/enums';
import { isValidWebsiteUrl, parseWebsiteUrlLines } from '@kanbu/schema/shared';
import { useLingui } from '@lingui/react/macro';
import { Link } from '@tanstack/react-router';
import type { TypedFormState } from '@utima/ui-informed';
import { useFieldState } from 'informed';
import { useCallback } from 'react';

import { Card } from '@/components/card/Card';
import { optionsFromEnum } from '@/components/form/formUtils';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';
import { ModelPicker } from '@/features/models/ModelPicker';
import { ReasoningEffortField } from '@/features/models/ReasoningEffortField';
import { useEnumTranslations } from '@/hooks';

/**
 * Live (speech-to-speech) model picker for voice chats — `voiceConfig.model`
 * — with the thinking-level field right below it, since which models offer
 * thinking depends on the model chosen here.
 */
function VoiceLiveModelPicker() {
  const { t } = useLingui();
  const translations = useEnumTranslations();
  const { schema } = useFieldsSchema(
    [
      {
        name: 'voiceConfig.model',
        label: t`Model`,
        type: 'select',
        options: optionsFromEnum(GeminiLiveModel),
        defaultValue: DEFAULT_GEMINI_LIVE_MODEL,
        showOptional: false,
      },
      {
        $relevant: {
          $when: ({ formState }) =>
            geminiLiveModelSupportsThinking(
              (
                formState as TypedFormState<{
                  voiceConfig?: { model?: string };
                }>
              ).values?.voiceConfig?.model ?? DEFAULT_GEMINI_LIVE_MODEL,
            ),
          $fields: [
            {
              name: 'voiceConfig.gemini.thinkingLevel',
              label: t`Thinking level`,
              type: 'select',
              options: translations.voiceThinkingLevel,
              defaultValue: 'minimal',
              tooltip: t`Higher thinking levels let the model reason more before responding, at the cost of latency. Minimal is fastest and recommended for real-time voice conversations. Not offered on models that don't support it.`,
              showOptional: false,
              keepState: true,
            },
          ],
        },
      },
    ],
    [t, translations],
  );

  return <SchemaFields schema={schema} />;
}

export function AdvancedConfigFields() {
  const { t } = useLingui();
  const translations = useEnumTranslations();
  const typeFieldState = useFieldState('agentType');

  const validateWebsiteUrlLines = useCallback(
    (value: unknown) => {
      const entries = parseWebsiteUrlLines(value);

      if (entries.every(entry => isValidWebsiteUrl(entry))) {
        return;
      }

      return t`Enter a valid domain or URL on each line`;
    },
    [t],
  );

  const { schema: topSchema } = useFieldsSchema(
    [
      {
        name: 'chatbotConfig.requireAcknowledgement',
        label: t`Require disclaimer acknowledgement`,
        tooltip: t`User has to accept the disclaimer before using the chatbot`,
        type: 'checkbox',
        uiControl: 'switch',
        defaultValue: false,
        showOptional: false,
      },
      {
        $relevant: {
          $when: ({ formState }) =>
            !!(
              formState as TypedFormState<{
                chatbotConfig?: {
                  requireAcknowledgement?: boolean;
                };
              }>
            ).values?.chatbotConfig?.requireAcknowledgement,
          $fields: [
            {
              name: 'chatbotConfig.disclaimerContent',
              label: t`Liability disclaimer content`,
              type: 'richtext',
              required: false,
              showOptional: true,
            },
          ],
        },
      },
    ],
    [t],
  );

  const { schema: bottomSchema } = useFieldsSchema(
    [
      {
        /**
         * Web search itself is surface-agnostic — `resolveTools.ts` gates it
         * only on `hasWebSearch`, not `agentType` — so a voice chat gets the
         * exact same tool through `buildVoiceRunContext`'s shared
         * `resolveToolsForChat` call once this is on. Chat-only fields below
         * (`showToolActivity`, `showReasoning`) stay Chat-only: those are
         * widget-display concerns with nothing to render on a phone call.
         */
        $relevant: {
          $when: ({ formState }) => {
            const agent = (formState as TypedFormState<{ agent?: Agent }>)
              .values?.agent;

            return (
              agent === Agent.Conversational || agent === Agent.ShopAssistant
            );
          },
          $fields: [
            {
              name: 'agentConfig.hasWebSearch',
              label: t`Web search`,
              type: 'checkbox',
              uiControl: 'switch',
              defaultValue: false,
            },
          ],
        },
      },
      {
        $relevant: {
          $when: ({ formState }) =>
            (
              formState as TypedFormState<{
                agentType?: AgentType;
              }>
            ).values?.agentType === AgentType.Chat,
          $fields: [
            {
              name: 'agentConfig.showToolActivity',
              label: t`Show tool activity`,
              tooltip: t`While the assistant works with tools, it streams short status updates written in the user's language (e.g. "Searching the knowledge base…"). Internal tool names are never shown to the user.`,
              type: 'checkbox',
              uiControl: 'switch',
              defaultValue: false,
              showOptional: false,
            },
          ],
        },
      },
      {
        $relevant: {
          $when: ({ formState }) =>
            (
              formState as TypedFormState<{
                agentType?: AgentType;
              }>
            ).values?.agentType === AgentType.Chat,
          $fields: [
            {
              name: 'agentConfig.showReasoning',
              label: t`Show reasoning`,
              tooltip: t`Show the model's thinking steps above its answer.`,
              type: 'checkbox',
              uiControl: 'switch',
              defaultValue: false,
              showOptional: false,
            },
          ],
        },
      },
      {
        $relevant: {
          $when: ({ formState }) =>
            !!(formState as TypedFormState<{ agentConfig: AgentConfig }>).values
              ?.agentConfig?.showToolActivity,
          $fields: [
            {
              name: 'chatbotConfig.expandToolActivity',
              label: t`Expand tool activity by default`,
              tooltip: t`When on, the activity trail starts expanded while the assistant works — showing each step's detail as it happens — and folds to a one-line summary once the answer starts. When off, it stays a single calm line the user can expand on demand.`,
              type: 'checkbox',
              uiControl: 'switch',
              defaultValue: false,
              showOptional: false,
            },
          ],
        },
      },
      {
        $relevant: {
          $when: ({ formState }) =>
            !!(formState as TypedFormState<{ agentConfig: AgentConfig }>).values
              ?.agentConfig?.hasWebSearch,
          $fields: [
            {
              name: 'agentConfig.webSearchUrls',
              label: t`Web search URLs (optional)`,
              type: 'text',
              uiProps: {
                rows: 5,
                placeholder:
                  'example.com\nexample.com/blog\nhttps://docs.example.com/help',
              },
              uiControl: 'textarea',
              tooltip: t`Optional domain allowlist. Leave empty to search the open web. When set, enter one URL or domain per line (example.com, example.com/blog, or https://example.com/page).`,
              description: t`Optional. Empty = unrestricted web search. One domain or URL per line when restricting.`,
              parser: parseWebsiteUrlLines,
              clean: parseWebsiteUrlLines,
              mask: value => parseWebsiteUrlLines(value).join('\n'),
              validate: validateWebsiteUrlLines,
              validateOn: 'blur-submit',
              showErrorIfTouched: true,
            },
          ],
        },
      },
      {
        // Inert on voice alongside `showSources` itself (v3 R1 forces
        // `showSources` to `false` in `buildVoiceRunContext`), so these
        // "how to display sources" fields are meaningless without it.
        // `keepState` on both so re-enabling Chat restores the choice.
        $relevant: {
          $when: ({ formState }) =>
            (
              formState as TypedFormState<{
                agentType?: AgentType;
                agentConfig?: {
                  showSources?: boolean;
                };
              }>
            ).values?.agentType !== AgentType.Voice &&
            !!(
              formState as TypedFormState<{
                agentConfig?: {
                  showSources?: boolean;
                };
              }>
            ).values?.agentConfig?.showSources,
          $fields: [
            {
              name: 'chatbotConfig.sourcesDisplayMode',
              label: t`Sources display mode`,
              type: 'select',
              options: translations.sourcesDisplayMode,
              defaultValue: SourcesDisplayMode.Pills,
              tooltip: t`Pills shows compact source chips under the answer. Structured renders a dedicated sources block.`,
              keepState: true,
            },
            {
              name: 'chatbotConfig.inlineCitations',
              label: t`Inline citation superscripts`,
              type: 'checkbox',
              uiControl: 'switch',
              defaultValue: false,
              showOptional: false,
              tooltip: t`Adds small numbered superscripts (¹ ²) next to the claims they support, linking to the source. Off shows provenance only as the sources list at the end of the answer.`,
              keepState: true,
            },
          ],
        },
      },
      {
        $relevant: {
          $when: ({ formState }) =>
            (
              formState as TypedFormState<{
                agentType?: AgentType;
                agentConfig?: {
                  showSources?: boolean;
                };
                chatbotConfig?: {
                  sourcesDisplayMode?: SourcesDisplayMode;
                };
              }>
            ).values?.agentType !== AgentType.Voice &&
            !!(
              formState as TypedFormState<{
                agentConfig?: {
                  showSources?: boolean;
                };
              }>
            ).values?.agentConfig?.showSources &&
            ((
              formState as TypedFormState<{
                chatbotConfig?: {
                  sourcesDisplayMode?: SourcesDisplayMode;
                };
              }>
            ).values?.chatbotConfig?.sourcesDisplayMode ??
              SourcesDisplayMode.Pills) === SourcesDisplayMode.Structured,
          $fields: [
            {
              name: 'chatbotConfig.sourcesFormat',
              label: t`Sources format`,
              type: 'select',
              options: translations.sourcesFormat,
              defaultValue: SourcesFormat.Simple,
              tooltip: t`This setting will affect the format of the sources in the response.`,
              keepState: true,
            },
          ],
        },
      },
    ],
    [t, translations, validateWebsiteUrlLines],
  );

  return (
    <Card title={t`Advanced configuration`}>
      <SchemaFields schema={topSchema} />
      {typeFieldState.value === AgentType.Voice ? (
        <VoiceLiveModelPicker />
      ) : (
        <>
          <ModelPicker />
          <ReasoningEffortField />
        </>
      )}
      <ModelPicker auxiliary />
      {typeFieldState.value === AgentType.Chat && (
        <Link
          to='/models'
          className='mb-4 inline-block text-xs text-blue-600 hover:underline'
          target='_blank'
        >
          {t`Compare model costs and performance`}
        </Link>
      )}
      <SchemaFields schema={bottomSchema} />
    </Card>
  );
}
